import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { OwnerCategory } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { ValidatorsService } from './validators.service';

export type QuoteGlobalValidators = {
  requiredValidators: Array<ValidatorFn>;
  termValidators: Array<ValidatorFn>;
  wholeValidators: Array<ValidatorFn>;
  jointAppValidators: Array<ValidatorFn>;
};

@Injectable()
export class FormBuilderService {
  constructor(private fb: FormBuilder, private validationService: ValidatorsService) {}

  createForm(): FormGroup {
    const { fb } = this;
    const { required, email } = Validators;
    const quoteForm = fb.group(
      {
        convertToExisting: [],
        producerId: [, [required]],
        clientInformation: this.createClientInformation(),
        commissionSplits: [, [required]],
        corpAgency: [],
        productType: [, [required]],
        termQuote: this.createTermQuote(),
        wholeLifeQuote: this.createWholeLifeQuote(),
        jointAppQuote: this.createJointAppQuote(),
        ownerEmail: ['', [email], [this.validationService.conversionOwnerEmailValidator]],
        eligibilityCheck: [''],
        changingOwner: [''],
        conversionAddress: this.createConversionAddress(),
        isAmendment: [false],
        primaryPolicyAgentSelection: [],
        primaryAgentName: [''],
      },
      {
        validators: [],
        asyncValidators: [this.validationService.agentLicenseValidator],
      }
    );
    // Disabling conversion address bcoz quote was failing and we need this only in conversion component
    quoteForm.get('conversionAddress').disable();
    return quoteForm;
  }

  createGlobalValidators(): QuoteGlobalValidators {
    return {
      requiredValidators: [this.validationService.agentPhoneNumberValidator],
      termValidators: [
        this.validationService.termMinFaceAmountValidator,
        this.validationService.termMaxFaceAmountValidator,
        this.validationService.termLengthValidator,
        this.validationService.ecpValidator,
        this.validationService.termWaiverOfPremiumValidator,
        this.validationService.maxAgeArtValidator,
        this.validationService.termPremiumValidator,
      ],
      wholeValidators: [
        this.validationService.wholeMinFaceAmountValidator,
        this.validationService.wholeMaxFaceAmountValidator,
        this.validationService.wholeProductValidator,
        this.validationService.taxBracketValidator,
        this.validationService.wholeGirValidator,
        this.validationService.wholeRtrValidator,
        this.validationService.debouncedWholeCirValidator,
        this.validationService.debouncedWholeAlirValidator,
        this.validationService.wholeExchange1035Validator,
        this.validationService.wholeWaiverOfPremiumValidator,
        this.validationService.wholePremiumValidator,
      ],
      jointAppValidators: [
        this.validationService.jointAppTermMinFaceAmountValidator,
        this.validationService.jointAppTermMaxFaceAmountValidator,
        this.validationService.jointAppWholeMinFaceAmountValidator,
        this.validationService.jointAppWholeMaxFaceAmountValidator,
        this.validationService.jointAppTermLengthValidator,
        this.validationService.jointAppEcpValidator,
        this.validationService.jointAppWholeProductValidator,
        this.validationService.jointAppTaxBracketValidator,
        this.validationService.jointAppFaceAmountValidator,
        this.validationService.jointGirValidator,
        this.validationService.jointExchange1035Validator,
        this.validationService.jointRtrValidator,
        this.validationService.debouncedJointCirValidator,
        this.validationService.debouncedJointAlirValidator,
        this.validationService.jointWholeWaiverOfPremiumValidator,
        this.validationService.jointTermWaiverOfPremiumValidator,
        this.validationService.jointAppMaxPremiumValidator,
        this.validationService.jointAppMaxAgeArtValidator,
      ],
    };
  }

  private createClientInformation(): FormGroup {
    const { fb } = this;
    const { required } = Validators;
    return fb.group({
      ownerCategory: [OwnerCategory.INDIVIDUAL, [required]],
      ownerFirstName: ['', [required, this.validationService.isName]],
      ownerLastName: ['', [required, this.validationService.isName]],
      ownerEmail: [
        '',
        [required, this.validationService.emailFormatValidator],
        [this.validationService.ownerEmailValidator],
      ], // remove ownerEmailValidator when we remove the enableMultipleAdvisorsOneCustomer FeatureFlag
      ownerEntityName: [''],
      ownerEntityType: [''],
      ownerTrusteeType: [''],
      ownerTrusteeEntityName: [''],
      ownerEqualsInsured: [true],
      contractState: ['', [required]],
      insuredFirstName: [, [required, this.validationService.isName]],
      insuredLastName: [, [required, this.validationService.isName]],
      phoneNumber: [],
      gender: [, [required]],
      dob: [, [required, this.validationService.dobValidator]],
      relationshipToTrust: [''],
      healthClass: [, [required]],
      customerId: [],
      citizenship: ['citizen', [required]],
    });
  }

  // ownerInformation is a subset of ClientInformation replicated once per available owner classification
  createOwnerInformation(): FormGroup {
    const { fb } = this;
    const createOwnerGroup = (): FormGroup => {
      const controls = this.createClientInformation().controls;
      return fb.group({
        ownerFirstName: controls.ownerFirstName,
        ownerLastName: controls.ownerLastName,
        ownerEmail: controls.ownerEmail,
        ownerEntityName: controls.ownerEntityName,
        ownerEntityType: controls.ownerEntityType,
        ownerTrusteeType: controls.ownerTrusteeType,
        ownerTrusteeEntityName: controls.ownerTrusteeEntityName,
        contractState: controls.contractState,
        customerId: controls.customerId,
        hasExistingOwner: [false],
      });
    };
    const result = fb.group({
      individual: createOwnerGroup(),
      business: createOwnerGroup(),
    });
    result.get('business.ownerEntityName').setValidators(Validators.required);
    result.get('business.ownerEntityType').setValidators(Validators.required);
    result.get('business.ownerTrusteeType').setValidators(Validators.required);
    result.get('business.ownerTrusteeEntityName').setValidators(Validators.required);
    return result;
  }

  private createTermQuote(): FormGroup {
    const { fb } = this;
    const { required, min, max } = Validators;
    return fb.group({
      faceAmount: [, [required]],
      coverageTerm: [, [required]],
      waiverOfPremium: [false],
      ecp: [false],
      premium: [0, [required, min(1)]],
      waiver: [0],
      paymentFrequency: [ProductApiTypes.PaymentFrequency.Annual, [required]],
      maxFaceAmount: [],
      flatExtras: [],
      uwFlatExtras: [],
      product: [],
      type: [],
      productType: [],
      maxTerm: [],
      annualBaseRates: [[]],
      annualWaiverRates: [[]],
      effectiveDate: [],
      healthClass: [],
      productPremiumVersion: [],
      maxTermECP: [],
      isSaveAge: [],
      applicationSignedDate: [],
      showPremiumToClient: [true],
      initialInvoice: [0],
      maxPremium: [],
      originalTermPremium: [],
      policyNumber: [],
      isAutoPay: [],
      __version__: [],
      repriceWaiver: [0],
      repricePremium: [0],
      repriceFlatExtras: [],
      repriceAnnualBaseRates: [[]],
      repriceAnnualWaiverRates: [[]],
      useReprice: [false],
      submittedAt: [],
    });
  }

  private createConversionAddress(): FormGroup {
    const { fb } = this;
    const { required } = Validators;
    return fb.group({
      street: ['', [required]],
      line1: [],
      city: ['', [required]],
      state: ['', [required, this.validationService.isNYValidator]],
      zip: ['', [required], [this.validationService.zipCodeValidator]],
    });
  }

  createSolveForm(): FormGroup {
    const { fb } = this;
    const { required, min } = Validators;
    return fb.group({
      desiredPremium: [0, [required]],
      premiumSolveToggle: [false],
    });
  }

  private createWholeLifeQuote(): FormGroup {
    const { fb } = this;
    const { required, min, max } = Validators;
    return fb.group({
      faceAmount: [, [required]],
      product: [, [required]],
      waiverOfPremium: [false],
      dividendOption: [, [required]],
      loanType: [, [required]],
      taxBracket: [, [min(1), max(99)]],
      premium: [0, [required, min(1)]],
      annualPremium: [0],
      waiver: [0],
      paymentFrequency: [ProductApiTypes.PaymentFrequency.Annual, [required]],
      type: [],
      maxPremium: [],
      dividendInterestAdjustment: [],
      maxFaceAmount: [],
      flatExtras: [],
      uwFlatExtras: [],
      coverageTerm: [],
      productType: [],
      initialPremium: [],
      skipFirstMonth: [],
      riders: fb.group({
        gir: fb.group({
          enabled: [false],
          amount: [0],
          premium: [0],
          waiver: [0],
          flatExtras: [],
        }),
        rtr: fb.group({
          enabled: [false],
          amount: [0],
          premium: [0],
          waiver: [0],
          flatExtras: [],
        }),
        alir: fb.group({
          enabled: [false],
          amount: [0],
          premium: [0],
          waiver: [0],
          plannedYears: [0],
          paymentFrequency: [''],
          hasScheduledPayments: [],
        }),
        cir: fb.group({
          enabled: [false],
          amount: [null],
          benefitDisbursementMethod: [2],
          predictedBenefitAge: [75],
          puaAddedToBenefitPool: [false],
          monthlyIncreaseOption: [false],
          premium: [0],
          waiver: [0],
        }),
      }),
      exchange1035: fb.group({
        enabled: [false],
        cashValueTransfer: [0, [required]],
        costBasis: [0, [required, min(1)]],
        policyType: ['', [required]],
        applyTowards: ['', [required]],
        expectedArrival: [0, [required]],
        isMec: [false],
      }),
      bankSale: [],
      effectiveDate: [],
      healthClass: [],
      productPremiumVersion: [],
      isSaveAge: [],
      applicationSignedDate: [],
      showPremiumToClient: [true],
      initialInvoice: [0],
      globalError: [],
      wholeAPL: [],
      totalTargetPremium: [0],
      existingWholeLifePoliciesToChooseFrom: [],
      selectedExistingWholeLifePolicy: [],
      originalWholePremium: [],
      additionalFaceAmountToBeAdded: [0],
      isAutoPay: [],
      __version__: [],
    });
  }

  private createJointAppQuote(): FormGroup {
    const { fb } = this;
    const { required } = Validators;
    return fb.group({
      termQuote: this.createTermQuote(),
      wholeLifeQuote: this.createWholeLifeQuote(),
      paymentFrequency: [ProductApiTypes.PaymentFrequency.Annual, [required]],
      isAutoPay: [],
    });
  }
}
